import { useEffect, useState } from 'react';
import { TypedEventTarget } from 'typescript-event-target';

export const IS_PROD = import.meta.env.REACT_APP_CURRENT_ENV === 'production';

export const FLAG_REGISTRY: Array<LocalFlag<any>> = [];

export type LocalFlagSupportedTypes = boolean | number | string;

export class LocalFlag<
  T extends LocalFlagSupportedTypes = boolean,
> extends TypedEventTarget<{
  changed: CustomEvent<void>;
}> {
  private _name: string;
  private _description: string;
  private _devOnly: boolean;
  private _default: T;

  constructor(
    /** A short name for this flag. Will be used for this flag's state in LocalStorage, so it must be unique there. */
    name: string,

    /** What does this flag mean? */
    description: string,

    defaultValue: T,

    /** Setting this `true` means that this flag's value can *only* be true for developers (local environments, development and testing clusters) */
    devOnly?: boolean
  ) {
    super();

    FLAG_REGISTRY.push(this);

    this._name = name;
    this._description = description;
    this._default = defaultValue;
    this._devOnly = devOnly ?? false;

    if (IS_PROD && this._devOnly) return;

    if (localStorage.getItem(this._name) === null)
      localStorage.setItem(this._name, JSON.stringify(this._default));

    window.addEventListener('storage', ({ key, newValue, oldValue }) => {
      if (key !== this._name) return;

      if (oldValue === newValue) return;

      this.dispatchTypedEvent('changed', new CustomEvent('changed'));
    });
  }

  get value() {
    if (IS_PROD && this._devOnly) return this._default;

    const raw = localStorage.getItem(this._name);

    if (raw === null) return this._default;

    return JSON.parse(raw);
  }

  set value(newValue: T) {
    if (IS_PROD && this._devOnly) return;

    const current = localStorage.getItem(this._name);
    const upd = JSON.stringify(newValue);

    if (current === upd) return;

    localStorage.setItem(this._name, upd);
    this.dispatchTypedEvent('changed', new CustomEvent('changed'));
  }

  /** an alternate way to write a value that avoids some compatibility issues with RC */
  set(newValue: T) {
    this.value = newValue;
  }

  get description() {
    return this._description;
  }

  get name() {
    return this._name;
  }
}

export function useLocalFlagValue<T extends LocalFlagSupportedTypes>(
  flag: LocalFlag<T>
) {
  const [value, setValue] = useState(flag.value);

  useEffect(() => {
    const listener = () => {
      setValue(flag.value);
    };

    flag.addEventListener('changed', listener);
    return () => {
      flag.removeEventListener('changed', listener);
    };
  }, [flag]);

  return value;
}

export const FORCE_OUTDATED_BANNER = new LocalFlag<boolean>(
  'FORCE_OUTDATED_BANNER',
  'Forces the client to display the "there\'s an update available" banner',
  false,
  true
);

export const USE_OLD_SCANNING = new LocalFlag<boolean>(
  'USE_OLD_SCANNING',
  'Prefer old scanning instead of new scanning.',
  false
);

export const DISABLE_OLD_SCANNING_PAGE_LIMITS = new LocalFlag<boolean>(
  'DISABLE_OLD_SCANNING_PAGE_LIMITS',
  'Remove the 100-page cap in the old Dynamsoft-based indexing page.',
  false
);

export const DISABLE_SHARED_VIEWER = new LocalFlag<boolean>(
  'DISABLE_SHARED_VIEWER',
  `Disables using a shared Webviewer component. This was a memory optimization because the Webviewer didn't shut down correctly.`,
  false,
  true
);

export const NEW_INDEXING_BARCODE_DPI = new LocalFlag<number>(
  'NEW_INDEXING_DPI',
  'DPI for rendering pages for the barcode reader. Set to -1 for value to be controlled by environment variable or hardcoded default.',
  -1
);
